import { useCallback, useState } from "react";
import { Box, Typography } from "@mui/material";
import type { Result } from "neverthrow";
import { err, errAsync, ok, okAsync, ResultAsync } from "neverthrow";
import { useNavigate } from "react-router";

import type {
  ConflictError,
  InternalError,
  InvalidError,
  NotFoundError,
} from "@thalamos/common";
import { buildInternalError, buildInvalidError } from "@thalamos/common";
import {
  Base64FileUpload,
  base64PayloadOf,
  decodeBase64,
} from "@thalamos/frontend";
import type { UseSingleUseActionTokenResponse } from "@thalamos/aspire-common";

import type { UploadBulkImportCSVProps } from "../../types.js";
import { api } from "~/api.js";
import {
  Banner,
  BannerList,
  Button,
  renderErrorToast,
  renderSuccessToast,
} from "~/components/design-system/index.js";
import { routeFns } from "~/routePaths.js";

export function AssignPatientNhsNumberActionInner({
  singleUseActionToken,
  uploadCsvFn,
  onSuccess,
}: UploadBulkImportCSVProps) {
  const [validatedFile, setValidatedFile] = useState<string | null>(null);
  const [dryRunResult, setDryRunResult] = useState<Result<
    UseSingleUseActionTokenResponse,
    InvalidError | NotFoundError | ConflictError | InternalError
  > | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleFileUpload = useCallback(
    (files: { base64: string | ArrayBuffer }[]) => {
      setValidatedFile(null);
      setDryRunResult(null);
      setIsProcessing(true);

      const uploadedFile = files.length === 1 ? files[0] : undefined;
      if (uploadedFile === undefined) {
        renderErrorToast({ message: "Please upload precisely one CSV file" });
        setIsProcessing(false);
        return;
      }

      const base64Value = uploadedFile.base64;
      if (typeof base64Value !== "string") {
        renderErrorToast({
          message: "File could not be read as base64 string",
        });
        setIsProcessing(false);
        return;
      }

      const csvData = decodeBase64(base64PayloadOf(base64Value));

      // Dry run upload
      void uploadCsvFn(singleUseActionToken.id, csvData, true).match(
        (okValue) => {
          setDryRunResult(ok(okValue));
          setValidatedFile(csvData);
          setIsProcessing(false);
        },
        (error) => {
          setDryRunResult(err(error));
          renderErrorToast({ message: error.message });
          setIsProcessing(false);
        },
      );
    },
    [singleUseActionToken.id, uploadCsvFn],
  );

  const handleConfirmImport = useCallback(async () => {
    if (!validatedFile) return;
    setIsProcessing(true);

    const result = await uploadCsvFn(
      singleUseActionToken.id,
      validatedFile,
      false,
    );

    if (result.isOk()) {
      setValidatedFile(null);
      renderSuccessToast({
        message:
          "File uploaded successfully. Processing of patients may take up to one hour.",
      });
      onSuccess();
    } else {
      renderErrorToast({ message: result.error.message });
    }

    setIsProcessing(false);
  }, [validatedFile, singleUseActionToken.id, uploadCsvFn, onSuccess]);

  return (
    <Box data-testid="use-valid-token">
      <Typography variant="h4">Upload Bulk Import CSV</Typography>
      <Typography>
        Upload a .csv file matching the bulk import specification given to you
        by the Thalamos programme team
      </Typography>

      <Box sx={{ my: 2 }}>
        <Base64FileUpload
          disabled={isProcessing}
          label="Upload CSV"
          accept=".csv"
          onDone={handleFileUpload}
        />
      </Box>

      {dryRunResult?.isErr() && (
        <Box data-testid="file-invalid-banner">
          <Banner
            title="File upload failed"
            body={[
              dryRunResult.error.message,
              ...(dryRunResult.error.issues ?? []),
            ]}
            bannerType={BannerList.ERROR}
          />
        </Box>
      )}

      {validatedFile && dryRunResult?.isOk() && (
        <>
          <Box sx={{ mb: 2 }} data-testid="file-validated">
            <Banner
              title="File uploaded and validated, ready to import. Please confirm the import using the button below"
              bannerType={BannerList.CONFIRMATION}
            />
          </Box>

          <Button
            label="Confirm Import"
            disabled={isProcessing}
            testId="confirm-import-button"
            onClick={handleConfirmImport}
          />
        </>
      )}
    </Box>
  );
}

export function AssignPatientNhsNumberAction({
  token,
}: {
  token: UploadBulkImportCSVProps["singleUseActionToken"];
}) {
  const navigate = useNavigate();
  const uploadCsvFn = useCallback(
    (
      tokenId: string,
      csvData: string,
      dryRun: boolean,
    ): ResultAsync<
      UseSingleUseActionTokenResponse,
      InvalidError | NotFoundError | ConflictError | InternalError
    > => {
      return ResultAsync.fromPromise(
        api.singleUseActionToken.use(tokenId, csvData, dryRun),
        (err) =>
          buildInternalError({
            message: err instanceof Error ? err.message : String(err),
          }),
      ).andThen((response) => {
        if (response.status === 200) {
          // This flow produces no report — return the empty union member.
          return okAsync({ type: "assignPatientNhsNumber" as const });
        } else if (response.status === 400) {
          const data = response.data as unknown as {
            message?: string;
            issues?: string[];
          };
          const message =
            data?.message ?? "Unknown error - Please contact Thalamos";
          // Preserve per-row `issues` so the banner can list each failing row.
          return errAsync(buildInvalidError({ message, issues: data?.issues }));
        } else {
          // Non-validation failures (5xx, transport) are server errors — map to
          // InternalError without `issues`.
          const data = response.data as unknown as { message?: string };
          const message =
            data?.message ?? "Unknown error - Please contact Thalamos";
          return errAsync(buildInternalError({ message }));
        }
      });
    },
    [],
  );

  return (
    <AssignPatientNhsNumberActionInner
      singleUseActionToken={token}
      uploadCsvFn={uploadCsvFn}
      onSuccess={() => {
        navigate(routeFns.home());
      }}
    />
  );
}
